import { Auth, Implementation } from '@leagueplatform/auth';
import { backOff } from 'exponential-backoff';
import { getSettledConfig } from '@leagueplatform/config';
import { captureError } from '@leagueplatform/observability';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import {
  getUserProfile,
  GET_USER_PROFILE_MESSAGE_TYPE,
} from '@leagueplatform/user-profile-api';
import { queryClient } from '../components/react-query-provider-wrapper';

const handleUserProfileFetchOnAuthEmbeddedError = (error: unknown) => {
  captureError(error instanceof Error ? error : new Error(String(error)), {
    errorName: 'Embedded User Profile Fetch On Auth Error',
  });

  toastActions.add({
    type: TOAST_STATUS.ERROR,
    textId: 'ERROR_HEADING_GENERIC',
    contentId: 'ERROR_CAPTION_BACKEND',
    shouldAutoClose: true,
  });
};

// While this teardown is present, setup for this app load is already in progress
// or complete. Repeated calls should reuse it instead of starting another
// settled-config read or attaching another auth listener.
let teardownSubscription: (() => void) | undefined;

/**
 * Waits for settled server config once and, when the embedded fetch-on-auth
 * flag is enabled, subscribes a single embedded auth listener that fetches the
 * shared user profile query after auth becomes ready.
 *
 * Returns a teardown that prevents late async setup from attaching the auth
 * listener and unsubscribes the auth listener if it was already attached.
 */
export function createUserProfileFetchOnAuthEmbeddedSubscription() {
  if (teardownSubscription) {
    return teardownSubscription;
  }

  let isActive = true;
  let unsubscribeFromAuth = () => {};

  const fetchUserProfileOnAuthEmbedded = () => {
    if (!Auth.initialized || Auth.implementation !== Implementation.EMBEDDED) {
      return;
    }

    backOff(() => getUserProfile(), {
      startingDelay: 200,
      numOfAttempts: 4,
    })
      .then((userProfile) => {
        queryClient.setQueryData([GET_USER_PROFILE_MESSAGE_TYPE], userProfile);
      })
      .catch(handleUserProfileFetchOnAuthEmbeddedError);
  };

  getSettledConfig()
    .then((config) => {
      if (
        !isActive ||
        config['flag_user-profile_fetch-on-auth-embedded'] !== true
      ) {
        return;
      }

      unsubscribeFromAuth = Auth.addOnAuthListener(
        fetchUserProfileOnAuthEmbedded,
        true,
      );
    })
    .catch(() => {
      // If server config never settles successfully, skip this optional behavior.
    });

  teardownSubscription = () => {
    isActive = false;
    unsubscribeFromAuth();
    teardownSubscription = undefined;
  };

  return teardownSubscription;
}
